<template>
	<transition name="modal-fade">
		<div v-if="isModalVisible" class="modal-backdrop">
			<div
				class="modal"
				role="dialog"
				aria-labelledby="modalTitle"
				aria-describedby="modalDescription"
			>
				<header class="modal-header" id="modalTitle">
					<div class="modal-header-content">
						<div class="modal-title-section">
							<h2 class="modal-main-title">Customer Special Prices</h2>
						</div>
						<div class="modal-customer-info">
							<span class="customer-id">{{
								selectedCustomer.customer_id
							}}</span>
							<span class="separator">•</span>
							<span class="customer-name">{{
								selectedCustomer.customer_title
							}}</span>
							<span
								v-if="selectedCustomer.customer_nickname"
								class="customer-nickname"
							>
								({{ selectedCustomer.customer_nickname }})
							</span>
						</div>
					</div>
				</header>

				<!-- New Close Button positioned absolutely -->
				<button
					class="modal-close-absolute"
					@click="closeModal"
					aria-label="Close modal"
				>
					<span class="close-x">✕</span>
					<span class="close-text">Close</span>
				</button>

				<section class="modal-body" id="modalDescription">
					<!-- Error Message Display -->
					<b-message
						v-if="errorMessage"
						type="is-danger"
						:closable="true"
						@close="errorMessage = null"
					>
						{{ errorMessage }}
					</b-message>

					<!-- Success Message Display -->
					<b-message
						v-if="successMessage"
						type="is-success"
						:closable="true"
						@close="successMessage = null"
					>
						{{ successMessage }}
					</b-message>

					<div class="special-prices-table">
						<table class="custom-table">
							<thead>
								<tr>
									<th class="th-stock-id">Stock ID</th>
									<th class="th-stock-name">Stock Name</th>
									<th class="th-price">Customer Price</th>
									<th class="th-actions">Actions</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(priceItem, index) in specialPrices"
									:key="`${priceItem.stock.stock_id}-${index}`"
								>
									<td>{{ priceItem.stock.stock_id }}</td>
									<td>{{ priceItem.stock.stock_name }}</td>
									<td>
										<span v-if="editingIndex !== index">
											{{ formatPrice(priceItem.customers_price) }}
										</span>
										<b-input
											v-if="editingIndex === index"
											v-model="editingPrice"
											type="number"
											size="is-small"
											min="0"
											step="1"
											:has-counter="false"
											@keyup.enter="savePrice(index)"
											@keyup.esc="cancelEdit"
										/>
									</td>
									<td>
										<div class="action-buttons">
											<button
												v-if="editingIndex !== index"
												class="btn-action btn-edit"
												@click="startEdit(index, priceItem)"
												title="Edit price"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="16"
													height="16"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													stroke-width="2"
													stroke-linecap="round"
													stroke-linejoin="round"
												>
													<path
														d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
													></path>
													<path
														d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
													></path>
												</svg>
												<span>Edit</span>
											</button>
											<template v-else>
												<button
													class="btn-action btn-save"
													@click="savePrice(index)"
													:disabled="isUpdating"
													title="Save changes"
												>
													<svg
														v-if="!isUpdating"
														xmlns="http://www.w3.org/2000/svg"
														width="16"
														height="16"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													>
														<polyline points="20 6 9 17 4 12"></polyline>
													</svg>
													<span v-if="!isUpdating">Save</span>
													<span v-else>Saving...</span>
												</button>
												<button
													class="btn-action btn-cancel"
													@click="cancelEdit"
													title="Cancel edit"
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="16"
														height="16"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														stroke-width="2"
														stroke-linecap="round"
														stroke-linejoin="round"
													>
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
													<span>Cancel</span>
												</button>
											</template>
											<button
												v-if="editingIndex !== index"
												class="btn-action btn-delete"
												@click="confirmDelete(priceItem)"
												:disabled="isDeleting"
												title="Delete price"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													width="16"
													height="16"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													stroke-width="2"
													stroke-linecap="round"
													stroke-linejoin="round"
												>
													<polyline points="3 6 5 6 21 6"></polyline>
													<path
														d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
													></path>
												</svg>
												<span>Delete</span>
											</button>
										</div>
									</td>
								</tr>
								<tr v-if="specialPrices.length === 0">
									<td colspan="4" class="has-text-centered">
										No special prices configured for this customer
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<!-- Add Special Price Button -->
					<div class="add-price-button-container">
						<button
							class="btn-add-special-price"
							@click="openAddPriceModal"
							type="button"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="18"
								height="18"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
							>
								<line x1="12" y1="5" x2="12" y2="19"></line>
								<line x1="5" y1="12" x2="19" y2="12"></line>
							</svg>
							<span>Add Special Price</span>
						</button>
					</div>
				</section>
			</div>
		</div>
	</transition>
</template>

<script>
import { ref, watch } from '@vue/composition-api'

export default {
	name: 'CustomerSpecialPricesModal',
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
		customer: {
			type: Object,
			default: () => ({}),
		},
	},
	setup(props, { emit }) {
		const isModalVisible = ref(false)
		const selectedCustomer = ref({})
		const specialPrices = ref([])
		const editingIndex = ref(null)
		const editingPrice = ref(null)
		const editingItem = ref(null)
		const isUpdating = ref(false)
		const isDeleting = ref(false)
		const errorMessage = ref(null)
		const successMessage = ref(null)

		watch(
			() => props.visible,
			(newVal) => {
				isModalVisible.value = newVal
			},
		)

		watch(
			() => props.customer,
			(newCustomer) => {
				if (newCustomer && newCustomer.customer_id) {
					selectedCustomer.value = newCustomer
					specialPrices.value = newCustomer.customer_price_lists || []
				}
			},
			{ immediate: true },
		)

		const formatPrice = (price) => {
			return new Intl.NumberFormat('sv-SE', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(price / 100)
		}

		const closeModal = () => {
			isModalVisible.value = false
			editingIndex.value = null
			editingPrice.value = null
			editingItem.value = null
			errorMessage.value = null
			successMessage.value = null
			emit('close')
		}

		const startEdit = (index, priceItem) => {
			editingIndex.value = index
			editingPrice.value = priceItem.customers_price
			editingItem.value = { ...priceItem }
		}

		const cancelEdit = () => {
			editingIndex.value = null
			editingPrice.value = null
			editingItem.value = null
		}

		const savePrice = async (index) => {
			// Clear previous messages
			errorMessage.value = null
			successMessage.value = null

			// Validation
			if (editingPrice.value === null || editingPrice.value === '') {
				errorMessage.value = 'Price cannot be empty'
				return
			}

			const price = parseInt(editingPrice.value)
			if (isNaN(price) || price < 0) {
				errorMessage.value = 'Please enter a valid price (must be 0 or greater)'
				return
			}

			if (price === editingItem.value.customers_price) {
				cancelEdit()
				return
			}

			isUpdating.value = true
			const priceItem = specialPrices.value[index]

			try {
				emit('update-price', {
					customer_id: selectedCustomer.value.customer_id,
					stock_id: priceItem.stock.stock_id,
					customers_price: price,
					index: index,
				})

				// Update local state immediately for better UX
				priceItem.customers_price = price
				successMessage.value = 'Price updated successfully'

				// Reset after success
				setTimeout(() => {
					isUpdating.value = false
					cancelEdit()
					successMessage.value = null
				}, 1500)
			} catch (error) {
				errorMessage.value = 'Failed to update price. Please try again.'
				isUpdating.value = false
			}
		}

		const confirmDelete = (priceItem) => {
			if (
				confirm(
					`Are you sure you want to delete the special price for ${priceItem.stock.stock_name}?`,
				)
			) {
				deletePrice(priceItem)
			}
		}

		const deletePrice = async (priceItem) => {
			// Clear previous messages
			errorMessage.value = null
			successMessage.value = null

			isDeleting.value = true

			try {
				emit('delete-price', {
					customer_id: selectedCustomer.value.customer_id,
					stock_id: priceItem.stock.stock_id,
				})

				// Remove from local state immediately for better UX
				const index = specialPrices.value.findIndex(
					(p) => p.stock.stock_id === priceItem.stock.stock_id,
				)
				if (index !== -1) {
					specialPrices.value.splice(index, 1)
				}

				successMessage.value = 'Price deleted successfully'

				setTimeout(() => {
					isDeleting.value = false
					successMessage.value = null
				}, 1500)
			} catch (error) {
				errorMessage.value = 'Failed to delete price. Please try again.'
				isDeleting.value = false
			}
		}

		const openAddPriceModal = () => {
			// Emit event to parent to open add price modal
			emit('open-add-modal')
		}

		return {
			isModalVisible,
			selectedCustomer,
			specialPrices,
			editingIndex,
			editingPrice,
			isUpdating,
			isDeleting,
			errorMessage,
			successMessage,
			formatPrice,
			closeModal,
			startEdit,
			cancelEdit,
			savePrice,
			confirmDelete,
			openAddPriceModal,
		}
	},
}
</script>

<style lang="scss" scoped>
// Import system fonts for better typography
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

// Typography variables
$font-family-base: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI',
	Roboto, 'Helvetica Neue', Arial, sans-serif;
$font-weight-normal: 400;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-weight-bold: 700;

.modal-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.75);
	backdrop-filter: blur(4px);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 11000;
	padding: 20px;
}

.modal {
	position: relative;
	background: white;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	max-width: 1000px;
	width: 100%;
	max-height: 90vh;
	border-radius: 12px;
	margin: auto;
	font-family: $font-family-base;
}

.modal-header {
	background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
	color: white;
	padding: 28px 35px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	border-bottom: none;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	position: relative;
	flex-shrink: 0;
	border-radius: 12px 12px 12px 12px;
	margin-top: 30px;
	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: linear-gradient(90deg, #4a90e2, #7b68ee, #4a90e2);
		background-size: 200% 100%;
		animation: shimmer 3s ease-in-out infinite;
	}

	.modal-header-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.modal-title-section {
		.modal-main-title {
			font-size: 1.75rem;
			font-weight: $font-weight-bold;
			margin: 0;
			color: white;
			letter-spacing: -0.025em;
			line-height: 1.2;
			font-family: $font-family-base;
		}
	}

	.modal-customer-info {
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 0.9375rem;
		color: rgba(255, 255, 255, 0.95);
		font-family: $font-family-base;
		line-height: 1.5;

		.customer-id {
			font-weight: $font-weight-semibold;
			font-family: 'SF Mono', Monaco, 'Courier New', monospace;
			font-size: 0.875rem;
			background: rgba(255, 255, 255, 0.15);
			padding: 2px 8px;
			border-radius: 4px;
		}

		.separator {
			color: rgba(255, 255, 255, 0.4);
			font-weight: $font-weight-normal;
		}

		.customer-name {
			font-weight: $font-weight-medium;
			letter-spacing: -0.01em;
		}

		.customer-nickname {
			font-style: normal;
			color: rgba(255, 255, 255, 0.85);
			font-weight: $font-weight-normal;
			font-size: 0.875rem;
		}
	}
}

// New absolute positioned close button
.modal-close-absolute {
	position: absolute;
	top: 30px;
	right: 30px;
	background: #dc3545;
	color: white;
	border: none;
	border-radius: 6px;
	padding: 10px 20px;
	font-family: $font-family-base;
	font-size: 0.9375rem;
	font-weight: $font-weight-semibold;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	z-index: 10;
	transition: all 0.2s ease;
	box-shadow: 0 4px 12px rgba(220, 53, 69, 0.25);

	.close-x {
		font-size: 1.125rem;
		line-height: 1;
	}

	.close-text {
		letter-spacing: 0.025em;
	}

	&:hover {
		background: #c82333;
		transform: translateY(-2px);
		box-shadow: 0 6px 16px rgba(220, 53, 69, 0.35);
	}

	&:active {
		transform: translateY(0);
		box-shadow: 0 2px 8px rgba(220, 53, 69, 0.25);
	}
}

.modal-body {
	padding: 30px 35px;
	overflow-y: auto;
	flex-grow: 1;
	background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);

	// Style for message components
	.message {
		margin-bottom: 20px;
		border-radius: 8px;
	}
}

// Add Special Price button
.add-price-button-container {
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid #e9ecef;
	display: flex;
	justify-content: flex-start;
}

.btn-add-special-price {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 10px 20px;
	background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
	color: white;
	border: none;
	border-radius: 6px;
	font-family: $font-family-base;
	font-size: 0.9375rem;
	font-weight: $font-weight-semibold;
	cursor: pointer;
	transition: all 0.2s ease;
	box-shadow: 0 2px 8px rgba(40, 167, 69, 0.2);

	svg {
		width: 18px;
		height: 18px;
		transition: transform 0.2s ease;
	}

	&:hover {
		background: linear-gradient(135deg, #20c997 0%, #28a745 100%);
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);

		svg {
			transform: rotate(90deg);
		}
	}

	&:active {
		transform: translateY(0);
		box-shadow: 0 2px 6px rgba(40, 167, 69, 0.2);
	}
}

.special-prices-table {
	width: 100%;
	margin-top: 10px;

	.custom-table {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0;
		background: white;
		border-radius: 10px;
		overflow: hidden;
		box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
		border: 1px solid #e9ecef;

		thead {
			background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);

			tr {
				height: 50px;
			}

			th {
				padding: 16px 20px;
				text-align: left;
				font-family: $font-family-base;
				font-weight: $font-weight-semibold;
				font-size: 0.75rem;
				text-transform: uppercase;
				letter-spacing: 0.075em;
				color: #6c757d;
				border-bottom: 2px solid #dee2e6;
				position: relative;
				line-height: 1.5;

				&.th-stock-id {
					width: 20%;
				}

				&.th-stock-name {
					width: 40%;
				}

				&.th-price {
					width: 20%;
					text-align: right;
				}

				&.th-actions {
					width: 20%;
					text-align: center;
				}

				&::after {
					content: '';
					position: absolute;
					bottom: 0;
					left: 0;
					right: 0;
					height: 2px;
					background: linear-gradient(90deg, transparent, #4a90e2, transparent);
					transform: scaleX(0);
					transition: transform 0.3s ease;
				}

				&:hover::after {
					transform: scaleX(1);
				}
			}
		}

		tbody {
			tr {
				transition: all 0.2s ease;
				border-bottom: 1px solid #f0f0f0;

				&:hover {
					background-color: #f8f9fa;
					transform: translateX(5px);
					box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
				}

				&:last-child {
					border-bottom: none;
				}

				td {
					padding: 20px;
					font-family: $font-family-base;
					font-size: 0.9375rem;
					color: #495057;
					vertical-align: middle;
					line-height: 1.6;

					&:nth-child(1) {
						font-weight: $font-weight-medium;
						color: #6c757d;
						font-family: 'SF Mono', Monaco, 'Courier New', monospace;
						font-size: 0.875rem;
						letter-spacing: 0.025em;
					}

					&:nth-child(2) {
						color: #212529;
						font-weight: $font-weight-normal;
						letter-spacing: -0.01em;
					}

					&:nth-child(3) {
						text-align: right;
						font-weight: $font-weight-semibold;
						color: #2d5a87;
						font-size: 1.125rem;
						font-variant-numeric: tabular-nums;
						letter-spacing: -0.02em;
					}

					&:nth-child(4) {
						text-align: center;
					}
				}
			}

			tr[v-if] td {
				text-align: center;
				padding: 40px 20px;
				color: #6c757d;
				font-style: italic;
				font-size: 0.95rem;
			}
		}

		// Input styling in edit mode
		.b-input {
			max-width: 150px;
			margin: 0 auto;
		}
	}
}

// Professional button styling
.action-buttons {
	display: flex;
	gap: 8px;
	justify-content: center;

	.btn-action {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 7px 14px;
		border: none;
		border-radius: 6px;
		font-family: $font-family-base;
		font-size: 0.8125rem;
		font-weight: $font-weight-medium;
		letter-spacing: 0.015em;
		line-height: 1.5;
		cursor: pointer;
		transition: all 0.2s ease;
		position: relative;
		overflow: hidden;
		background: white;

		svg {
			width: 16px;
			height: 16px;
			transition: transform 0.2s ease;
		}

		span {
			transition: transform 0.2s ease;
		}

		&::before {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			width: 0;
			height: 0;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.3);
			transform: translate(-50%, -50%);
			transition: width 0.4s ease, height 0.4s ease;
		}

		&:hover::before {
			width: 100px;
			height: 100px;
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		// Edit button
		&.btn-edit {
			color: #4a90e2;
			border: 1px solid #4a90e2;

			&:hover:not(:disabled) {
				background: #4a90e2;
				color: white;
				transform: translateY(-2px);
				box-shadow: 0 4px 12px rgba(74, 144, 226, 0.3);

				svg {
					transform: rotate(5deg);
				}
			}
		}

		// Save button
		&.btn-save {
			background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
			color: white;
			border: none;

			&:hover:not(:disabled) {
				transform: translateY(-2px);
				box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
			}

			&:disabled {
				background: #cbd5e0;
			}
		}

		// Cancel button
		&.btn-cancel {
			color: #718096;
			border: 1px solid #cbd5e0;

			&:hover:not(:disabled) {
				background: #f7fafc;
				border-color: #a0aec0;
				transform: translateY(-1px);
			}
		}

		// Delete button
		&.btn-delete {
			color: #e53e3e;
			border: 1px solid #feb2b2;

			&:hover:not(:disabled) {
				background: linear-gradient(135deg, #fc5c5c 0%, #e53e3e 100%);
				color: white;
				border-color: transparent;
				transform: translateY(-2px);
				box-shadow: 0 4px 12px rgba(229, 62, 62, 0.3);

				svg {
					animation: shake 0.3s ease;
				}
			}
		}
	}
}

@keyframes shake {
	0%,
	100% {
		transform: translateX(0);
	}
	25% {
		transform: translateX(-2px);
	}
	75% {
		transform: translateX(2px);
	}
}

@keyframes shimmer {
	0% {
		background-position: -200% 0;
	}
	100% {
		background-position: 200% 0;
	}
}

.modal-fade-enter,
.modal-fade-leave-to {
	opacity: 0;
	transform: scale(0.95);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: all 0.3s ease;
}

.modal-fade-enter-to,
.modal-fade-leave {
	opacity: 1;
	transform: scale(1);
}

// Responsive design for mobile and tablets
@media screen and (max-width: 768px) {
	.modal-backdrop {
		padding: 10px;
	}

	.modal {
		max-width: 100%;
		max-height: 95vh;
		border-radius: 8px;
	}

	.modal-header {
		padding: 20px 20px;

		.modal-title-section {
			.modal-main-title {
				font-size: 1.25rem;
			}
		}

		.modal-customer-info {
			flex-wrap: wrap;
			gap: 8px;
			font-size: 0.875rem;

			.customer-id {
				font-size: 0.8125rem;
			}

			.separator {
				display: none;
			}
		}
	}

	.modal-body {
		padding: 20px;
		padding-top: 60px; // Extra space for the close button
	}

	.modal-close-absolute {
		top: 15px;
		right: 15px;
		padding: 8px 16px;
		font-size: 0.875rem;

		.close-x {
			font-size: 1rem;
		}
	}

	.special-prices-table {
		.custom-table {
			font-size: 0.875rem;

			thead th {
				padding: 12px 10px;
				font-size: 0.7rem;

				&.th-stock-name {
					width: 35%;
				}

				&.th-actions {
					width: 25%;
				}
			}

			tbody td {
				padding: 14px 10px;
				font-size: 0.875rem;

				&:nth-child(1) {
					font-size: 0.8125rem;
				}

				&:nth-child(3) {
					font-size: 1rem;
				}
			}
		}
	}

	.action-buttons {
		flex-direction: column;
		gap: 6px;
		width: 100%;

		.btn-action {
			width: 100%;
			justify-content: center;
			padding: 8px 12px;
			font-size: 0.8125rem;

			span {
				display: inline;
			}
		}
	}
}

// Small mobile devices
@media screen and (max-width: 480px) {
	.modal-header {
		.modal-customer-info {
			flex-direction: column;
			align-items: flex-start;
			gap: 4px;
		}
	}

	.special-prices-table {
		overflow-x: auto;

		.custom-table {
			min-width: 450px;
		}
	}

	.action-buttons {
		.btn-action {
			span {
				font-size: 0.75rem;
			}
		}
	}
}

// Large screens
@media screen and (min-width: 1400px) {
	.modal {
		max-width: 1200px;
	}

	.modal-header {
		padding: 32px 40px;

		.modal-main-title {
			font-size: 2rem;
		}
	}

	.modal-body {
		padding: 35px 40px;
	}

	.special-prices-table {
		.custom-table {
			thead th {
				padding: 18px 24px;
				font-size: 0.8125rem;
			}

			tbody td {
				padding: 22px 24px;
				font-size: 1rem;

				&:nth-child(3) {
					font-size: 1.25rem;
				}
			}
		}
	}
}

// Dark mode support (if your app has dark mode)
@media (prefers-color-scheme: dark) {
	.modal {
		// Add dark mode styles here if needed
	}
}

// Print styles
@media print {
	.modal-backdrop {
		background: none;
	}

	.modal {
		box-shadow: none;
		max-width: 100%;
		max-height: none;
	}

	.modal-close-absolute,
	.action-buttons {
		display: none;
	}
}

// Accessibility - reduced motion
@media (prefers-reduced-motion: reduce) {
	* {
		animation-duration: 0.01ms !important;
		animation-iteration-count: 1 !important;
		transition-duration: 0.01ms !important;
	}
}
</style>
